import {Component, OnInit, TemplateRef} from '@angular/core';
import {LoaderService} from '../shared/services/loader.service';
import {ErrorHandlerService} from '../shared/services/error-handler.service';
import {AuthenticationService} from '../shared/services/authentication.service';
import {catchError, finalize, switchMap, tap} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {NavigationService} from '../shared/services/navigation.service';
import {
  PodSearchMode,
  PodState,
  PodValidation,
  PodValidationDeliveryType,
  TabResultCount
} from './shared/pod-validation.model';
import {PodValidationService} from './shared/pod-validation.service';
import {EMPTY, Observable} from 'rxjs';
import {PodValidationSearchCriteria} from './shared/pod-validation-search-criteria.model';
import {ModalService} from '../shared/services/modal.service';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {SearchPodValidationResult} from './shared/search-pod-validation-result.model';
import {Option} from '../shared/data-filters/data-filter-select/data-filter-select.component';
import {TmsType} from '../user-dashboard/shared/tms-type.model';
import {Carrier} from '../user-dashboard/shared/carrier.model';
import {RegionService} from '../user-dashboard/shared/region.service';
import {CarrierService} from '../user-dashboard/shared/carrier.service';
import * as moment from 'moment';

@Component({
  selector: 'app-pods-validation',
  templateUrl: './pods-validation.component.html',
  styleUrls: ['./pods-validation.component.scss']
})
export class PodsValidationComponent implements OnInit {

  public isCarrier = false;
  public isPodReviewer = false;
  public allCarriers: Carrier[] = [];
  public availableCarriers: Option[] = [];
  public availableRegions: Option[] = [];
  public selectedPod: PodValidation;
  public tabResultCount: TabResultCount = new TabResultCount(0, 0, 0);
  public lastPage = 1;
  public podsValidation: PodValidation[] = [];
  public filters: PodValidationSearchCriteria = {
    page: 1,
    pageSize: 10,
    podState: PodState.PENDING,
    regions: [],
    carriers: [],
    loadOrDeliveryNumbers: [],
    type: null,
    originGlid: [],
    destinationGlid: []
  };
  public lastSearchFilter = {...this.filters};
  public podSearchMode: PodSearchMode = PodSearchMode.DEFAULT;

  public POD_STATE = PodState;

  public originOptions: Option[]

  private readonly localStorageKey = 'pods-validation';

  constructor(
    private authService: AuthenticationService,
    private toastrService: ToastrService,
    private errorHandlerService: ErrorHandlerService,
    private navigationService: NavigationService,
    private loaderService: LoaderService,
    private podValidationService: PodValidationService,
    private regionService: RegionService,
    private carrierService: CarrierService,
    private modalService: ModalService,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private router: Router
  ) {
  }

  public ngOnInit() {
    this.isPodReviewer = this.authService.canManagePodsValidation();
    if (!this.isPodReviewer && !this.authService.canAccessPodsValidation()) {
      this.toastrService.error('You are not allowed to view this page');
      this.navigationService.home().go();
      return;
    } else if (!this.isPodReviewer && this.authService.canAccessPodsValidation()) {
      this.isCarrier = true;
    }

    if (!this.isCarrier) {
      this.regionService.getOwnRegions()
        .subscribe(regions => this.availableRegions = regions.map(region => {
          return {value: region.id, label: region.name}
        }));

      this.carrierService.getAllLightByTmsType(TmsType.BLUEYONDER)
        .subscribe(carriers => {
          this.allCarriers = carriers;
          this.availableCarriers = carriers.map(carrier => {
            return {value: carrier.id, label: carrier.name}
          });
        });
    }

    this.filters.podState = this.isCarrier ? PodState.PENDING : PodState.CHEP_REVIEW;

    this.route.queryParamMap.subscribe(
      (params: ParamMap) => this.initWithQueryParams(params)
    );
  }

  public reset(): void {
    this.filters = {
      page: this.filters.page,
      pageSize: this.filters.pageSize,
      podState: this.filters.podState,
      carriers: [],
      regions: [],
      type: null,
      originGlid: [],
      destinationGlid: [],
      loadOrDeliveryNumbers: []
    };

    this.beforeSearch();
  }

  public search(): void {
    this.beforeSearch();
    this.loaderService.startLoad();
    this.getSearchObservable().pipe(
      finalize(() => this.loaderService.stopLoad())
    ).subscribe();
  }

  public regionsChanged(regionIds: string[]) {
    if (regionIds.length > 0) {
      this.carrierService.getAllLightByRegions(this.filters.regions, TmsType.BLUEYONDER)
        .subscribe(carriers => {
          this.availableCarriers = carriers.map(carrier => {
            return {value: carrier.id, label: carrier.name}
          })

          this.filters.carriers = this.filters.carriers.filter(carrier => this.availableCarriers.map(c => c.value).includes(carrier));

          localStorage.setItem(`${this.localStorageKey}.regions`, JSON.stringify(regionIds));
          localStorage.setItem(`${this.localStorageKey}.carriers`, JSON.stringify(this.filters.carriers));
          this.updateQueryParams(this.filters.regions, this.filters.carriers, this.filters.podState, this.podSearchMode);
        });
    } else {
      this.availableCarriers = this.allCarriers.map(carrier => {
        return {value: carrier.id, label: carrier.name}
      });

      localStorage.setItem(`${this.localStorageKey}.regions`, JSON.stringify(regionIds));
      this.updateQueryParams(this.filters.regions, this.filters.carriers, this.filters.podState, this.podSearchMode);
    }
  }

  public carriersChanged(carrierIds: string[]) {
    localStorage.setItem(`${this.localStorageKey}.carriers`, JSON.stringify(carrierIds));
    this.updateQueryParams(this.filters.regions, this.filters.carriers, this.filters.podState, this.podSearchMode);
  }

  private updateQueryParams(regionIds: string[], carrierIds: string[], podState: PodState, mode: PodSearchMode) {
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: {
          regionIds,
          carrierIds,
          podState,
          mode
        }
      }
    );
  }

  public openConfirmExportPodsValidation(content: TemplateRef<any>) {
    this.modalService.open({
      title: this.translateService.instant('pods-validation.modal.export.title'),
      content,
      validateButton: {
        text: this.translateService.instant('pods-validation.modal.export.actions.export'),
        icon: 'fa fa-download'
      },
      closeButton: {
        text: this.translateService.instant('pods-validation.modal.export.actions.close'),
      },
      size: 'sm'
    }).pipe(
      tap(() => this.loaderService.startLoad()),
      switchMap(() => this.podValidationService.export(this.filters)),
      catchError(e => {
        this.errorHandlerService.handleHttpError(e, 'pods-validation.modal.export.error');
        return EMPTY;
      }),
      finalize(() => this.loaderService.stopLoad())
    ).subscribe();
  }

  public openConfirmSendPendingReminder(content: TemplateRef<any>) {
    this.modalService.open({
      title: this.translateService.instant('pods-validation.modal.reminder.title'),
      content,
      validateButton: {
        text: this.translateService.instant('pods-validation.modal.reminder.actions.export'),
        icon: 'fa fa-download'
      },
      closeButton: {
        text: this.translateService.instant('pods-validation.modal.reminder.actions.close'),
      },
      size: 'sm'
    }).pipe(
      tap(() => this.loaderService.startLoad()),
      switchMap(() => this.podValidationService.sendPodPendingReminder(this.lastSearchFilter.carriers[0])),
      finalize(() => this.loaderService.stopLoad())
    ).subscribe(
      () => this.toastrService.success(
        this.translateService.instant('pods-validation.modal.reminder.success', {
          carrierName: this.getSelectedCarrierName()
        })
      ),
      e => this.errorHandlerService.handleHttpError(e, 'pods-validation.modal.reminder.error')
    );
  }

  public getSelectedCarrierName() {
    return this.allCarriers.find(carrier => carrier.id === this.lastSearchFilter.carriers[0])?.name;
  }

  public onPodRejected(podValidation: PodValidation) {
    this.removePodFromList(podValidation);
    this.selectedPod = null;

    this.tabResultCount.chepReviewTotalCount--;
    this.tabResultCount.pendingTotalCount++;
  }

  public onPodValidated(podValidation: PodValidation) {
    this.removePodFromList(podValidation);
    this.selectedPod = null;

    this.tabResultCount.chepReviewTotalCount--;
    this.tabResultCount.validatedTotalCount++;
  }

  public onPodUploaded(podValidation: PodValidation) {
    this.removePodFromList(podValidation);

    this.tabResultCount.pendingTotalCount--;

    if (this.isCarrier) {
      this.tabResultCount.chepReviewTotalCount++;
    } else {
      this.tabResultCount.validatedTotalCount++;
    }
  }

  public onListFiltersChange(newFilters: PodValidationSearchCriteria) {
    this.filters = {...this.filters, ...newFilters};
    if (!this.disableSearch()) {
      this.search();
    }
  }

  appendOriginGlidFilter(glid: string) {
    if (!this.filters.originGlid.includes(glid)) {
      this.filters.originGlid.push(glid);
      this.onListFiltersChange(null);
    }
  }

  appendDestinationGlidFilter(glid: string) {
    if (!this.filters.destinationGlid.includes(glid)) {
      this.filters.destinationGlid.push(glid);
      this.onListFiltersChange(null);
    }
  }

  disableSearch() {
    return (
        !this.isCarrier &&
        (!this.filters.regions || this.filters.regions.length === 0) &&
        (!this.filters.carriers || this.filters.carriers.length === 0)
      ) &&
      (!this.filters.originGlid || this.filters.originGlid.length === 0) &&
      (!this.filters.destinationGlid || this.filters.destinationGlid.length === 0) &&
      (!this.filters.loadOrDeliveryNumbers || this.filters.loadOrDeliveryNumbers.length === 0) &&
      !this.filters.type &&
      !this.filters.onlyRejected &&
      !this.filters.podUploadedAfter &&
      !this.filters.podUploadedBefore;
  }

  private removePodFromList(podToRemove: PodValidation) {
    const podToRemoveIndex = this.podsValidation.findIndex(podValidation =>
      podValidation.loadNumber === podToRemove.loadNumber
      && podValidation.deliveryNumber === podToRemove.deliveryNumber);
    if (podToRemoveIndex !== -1) {
      this.podsValidation = [...this.podsValidation.slice(0, podToRemoveIndex), ...this.podsValidation.slice(podToRemoveIndex + 1)];
    }
  }

  private initWithQueryParams(params: ParamMap) {
    if (!this.isCarrier) {
      if (params.has('regionIds')) {
        this.filters.regions = params.getAll('regionIds');
      } else {
        this.filters.regions = JSON.parse(localStorage.getItem(`${this.localStorageKey}.regions`)) || [];
      }
      if (params.has('carrierIds')) {
        this.filters.carriers = params.getAll('carrierIds');
      } else {
        this.filters.carriers = JSON.parse(localStorage.getItem(`${this.localStorageKey}.carriers`)) || [];
      }
    }
    if (params.has('shipmentId')) {
      this.loaderService.startLoad();
      this.podValidationService.getById(params.get('shipmentId')).pipe(
        tap(result => {
          this.selectedPod = result;
          this.filters.loadOrDeliveryNumbers = [result.deliveryNumber]
        }),
        switchMap(() => this.getSearchObservable()),
        finalize(() => this.loaderService.stopLoad())
      ).subscribe();
    }
    if (params.has('mode')) {
      const lastWeek = moment().subtract(7, 'days').startOf('day')
      switch (<PodSearchMode>params.get('mode')) {
        case PodSearchMode.REJECTED:
          this.filters.onlyRejected = true;
          break;
        case PodSearchMode.DELIVERY_TODAY:
          this.filters.deliveryDateBefore = moment().endOf('day');
          this.filters.deliveryDateAfter = moment().startOf('day');
          break;
        case PodSearchMode.DELIVERY_LESS_THAN_ONE_WEEK:
          this.filters.deliveryDateBefore = moment().endOf('day');
          this.filters.deliveryDateAfter = lastWeek;
          break;
        case PodSearchMode.DELIVERY_MORE_THAN_ONE_WEEK:
          this.filters.deliveryDateBefore = lastWeek;
          break;
        case PodSearchMode.DEFAULT:
      }
    }
    if (params.has('state')) {
      this.filters.podState = <PodState>params.get('state');
    }
    if (this.filters.regions.length) {
      this.getSearchObservable().subscribe();
    }
  }

  private beforeSearch(): void {
    this.podsValidation = [];
    this.tabResultCount = new TabResultCount(0, 0, 0);
    this.selectedPod = null;
  }

  private getSearchObservable(): Observable<SearchPodValidationResult> {
    return this.podValidationService.search(this.filters)
      .pipe(
        tap(res => {
          this.podsValidation = res.results;
          this.tabResultCount = new TabResultCount(res.pendingTotalCount, res.chepReviewTotalCount, res.validatedTotalCount);
          this.lastPage = res.totalPages;
          this.lastSearchFilter = {...this.filters};
          this.originOptions = [
            {labelKey: 'pods-validation.filters.all', value: null},
            ...this.podsValidation
              .filter(value => value.type === PodValidationDeliveryType.MANUFACTURER)
              .map(podValidation => {
                return <Option>{
                  labelKey: podValidation.originName + ' (' + podValidation.originGlid + ')',
                  value: podValidation.originGlid
                }
              }).filter((option, i, a) =>
                a.findIndex(value => value.value === option.value) === i)
          ]
        }),
        catchError(e => {
          this.errorHandlerService.handleHttpError(e, 'pods-validation.errors.search-pods');
          return EMPTY;
        })
      )
  }
}
